import { AttributesMixin, define, Model, type } from "@type-r/models";
import { matchString } from "api/common";
import { asTimelessDate } from "lqx-react/lib";
import { AssetChange } from "./assetChange";
import { AssetEvent, AssetEventStatus } from "./assetEvent";
import { AssetType } from "./assetType";
import { CommonParticipant } from "./commonParticipant";
import { restfulIO } from "./endpoint";
import { Buyer, Invoice } from "./invoice";
import { PurchaseOrder } from "./purchaseOrder";

export interface InvoiceChange extends AttributesMixin<typeof InvoiceChange> { }
@define
export class InvoiceChange extends AssetChange {
    static endpoint = restfulIO('api/v1/ingest/invoices/assets');

    static attributes = {
        ...AssetChange.attributes,
        updateAsset: Invoice,
        assetType: type(Number).value(AssetType.invoice),
        assetEvents: type([AssetEvent]).value([]).dontSave
    };

    getAssetType() {
        return AssetType.invoice;
    }

    getAssetName() {
        return 'Invoice';
    }

    get number(): String {
        return this.getValue("number");
    }

    get currency(): String {
        return this.getValue("currency");
    }

    get buyer(): Buyer {
        return this.getValue("buyer") || new Buyer();
    }

    get supplier(): CommonParticipant {
        return this.getValue("supplier") || new CommonParticipant();
    }

    get purchaseOrder(): PurchaseOrder {
        return this.getValue("purchaseOrder") || new PurchaseOrder();
    }

    get invoiceDate(): Date {
        const value = this.getValue("invoiceDate");
        return value ? new Date(value) : null;
    }

    get invoiceDueDate(): Date {
        const value = this.getValue("invoiceDueDate");
        return value ? new Date(value) : null;
    }

    get anticipatedPaymentDate(): Date {
        const value = this.getValue("anticipatedPaymentDate");
        return value ? new Date(value) : null;
    }

    get anticipatedSettlementDate(): Date {
        const value = this.getValue("anticipatedSettlementDate");
        return value ? new Date(value) : null;
    }

    get effectiveDate(): Date {
        const value = this.getValue("effectiveDate");
        return value ? new Date(value) : null;
    }

    get fundingDate(): Date {
        const value = this.getValue("fundingDate");
        return value ? new Date(value) : null;
    }

    get faceValue(): String {
        return this.getValue("faceValue");
    }

    get adjustedFaceValue(): String {
        return this.getValue("adjustedFaceValue");
    }

    get adjustmentAmount(): String {
        return this.getValue("adjustmentAmount");
    }

    get outstandingAmountSeller(): String {
        return this.getValue("outstandingAmountSeller");
    }

    get relationshipIdentifier(): String {
        return this.getValue("relationshipIdentifier");
    }

    hasPendingAssetEventsFor(eventTypeId: number): boolean {
        return this.assetEvents.filter(ae => ae.status == AssetEventStatus.pending && ae.eventTypeId == eventTypeId).length > 0;
    }

    filter(filter) {
        return (matchString(this.number.toString(), filter.search) ||
            matchString(this.buyer.name, filter.search) ||
            matchString(this.faceValue.toString(), filter.search) ||
            matchString(asTimelessDate(this.invoiceDate), filter.search) ||
            matchString(this.state.name, filter.search) ||
            matchString(this.supplier.name, filter.search)) &&
            this.hasReceivedAssetEventsFor(filter.eventType);
    }
}

export interface InvoiceData extends AttributesMixin<typeof InvoiceData> { }
@define
export class InvoiceData extends Model {
    static endpoint = restfulIO('api/v1/ingest/invoices/assets');

    static attributes = {
        count: Number,
        items: [InvoiceChange]
    };
}